import React, { ReactNode, useRef, useState } from 'react';
import { useTheme } from '@/contexts/theme-context';
import { YStack } from '@usepepper/sprout-kit-ui';

export interface FileDropzoneProps {
  /** Comma-separated list of filetypes that are allowed to be uploaded. */
  accept: string;
  children: ReactNode;
  inputLabel: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  onRejectedFiles?: (files: File[]) => void;
}

const PADDING_VERTICAL = 28;
const PADDING_HORIZONTAL = 20;
const CONTENT_GAP = 6;
const BORDER_WIDTH = 1.5;

/**
 * Whether a file matches one of the `accept` tokens. Mirrors the semantics of
 * the file input's `accept` attribute (which only constrains the browse dialog,
 * not drag-and-drop): an extension (`.jpg`), a wildcard MIME (`image/*`), or an
 * exact MIME (`image/png`).
 */
const isFileAccepted = (file: File, acceptTokens: string[]): boolean => {
  if (acceptTokens.length === 0) {
    return true;
  }
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  const matchesExtension = acceptTokens.some(
    (token) => token.startsWith('.') && fileName.endsWith(token),
  );
  if (matchesExtension) {
    return true;
  }
  // Browsers report an empty type for some accepted formats (e.g. HEIC), so
  // when the type is unknown and no extension token settles it, defer to the
  // downstream validation rather than dropping a potentially valid file.
  if (!fileType) {
    return true;
  }
  return acceptTokens.some((token) => {
    if (token.endsWith('/*')) {
      return fileType.startsWith(token.slice(0, -1));
    }
    return fileType === token;
  });
};

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  accept,
  children,
  inputLabel,
  multiple = false,
  onFiles,
  onRejectedFiles,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const acceptTokens = accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);

  const emitFiles = (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : [];
    const accepted: File[] = [];
    const rejected: File[] = [];
    files.forEach((file) => {
      (isFileAccepted(file, acceptTokens) ? accepted : rejected).push(file);
    });
    onFiles(accepted);
    if (rejected.length > 0) {
      onRejectedFiles?.(rejected);
    }
  };

  return (
    <div
      onDragLeave={() => setIsDragOver(false)}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragOver(false);
        emitFiles(event.dataTransfer.files);
      }}
    >
      <YStack
        alignItems="center"
        backgroundColor={theme.sk.color.bg.primary.hover}
        borderColor={
          isDragOver
            ? theme.sk.color.border.upload.hover
            : theme.sk.color.border.upload.default
        }
        borderRadius={theme.sk.radii.radiusLarge}
        borderStyle="dashed"
        borderWidth={BORDER_WIDTH}
        cursor="pointer"
        gap={CONTENT_GAP}
        justifyContent="center"
        onPress={() => inputRef.current?.click()}
        paddingHorizontal={PADDING_HORIZONTAL}
        paddingVertical={PADDING_VERTICAL}
      >
        <>{children}</>
      </YStack>
      <input
        accept={accept}
        aria-label={inputLabel}
        hidden
        multiple={multiple}
        onChange={(event) => {
          emitFiles(event.target.files);
          event.target.value = '';
        }}
        ref={inputRef}
        type="file"
      />
    </div>
  );
};
